import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { capitalize } from '@recargas-dominicanas/core/utils';
import { getDateRangeOptions } from '@recargas-dominicanas/core/constants';
import { useBusinessStore } from '../../../stores/businessStore';
import { differenceInMonths } from 'date-fns';
import type { DateRange as DateRangeType } from 'react-day-picker';
import { AreaChart, BarChart, DateRange, OutlineCard } from '@recargas-dominicanas/core/components';
import { style } from './sales.module.css';

export function Sales() {

  const { 
    business, 
    sales: salesState, 
    fetchSales 
  } = useBusinessStore();

  const options = useMemo(getDateRangeOptions, []);

  const [dateRange, setDateRange] = useState<DateRangeType>(options[0]);
  const [period, setPeriod] = useState<'day' | 'month'>('day');;

  const loading = salesState.loading && salesState.transactions.length === 0;

  const sales = useMemo(() => salesState.transactions.map(({ date, sales }) => ({ 
    x: new Date(date), 
    y: { sales } 
  })), [salesState.transactions]);

  const profits = useMemo(() => salesState.transactions.map(({ date, profit }) => ({ 
    x: new Date(date), 
    y: { profit }
  })), [salesState.transactions]);

  const salesByProduct = useMemo(() => salesState.transactionsByProduct.map(({ date, ...item }) => ({ 
    x: new Date(date), 
    y: item
  })), [salesState.transactionsByProduct]);

  useEffect(() => {
    handleRefetch();
  }, []);

  async function handleDateRangeChange(dateRange: DateRangeType) {

    const period = dateRange.from && dateRange.to 
      ? differenceInMonths(dateRange.to, dateRange.from) < 4 ? 'day' : 'month'
      : 'day';

    setDateRange(dateRange);    
    setPeriod(period);
    
    await fetchSales(business!.id, dateRange.from, dateRange.to, period);
  }

  async function handleRefetch() {
    await fetchSales(business!.id, dateRange.from, dateRange.to, period);
  }

  function formatDate(date: Date) {
    return period === 'day'
      ? date.toLocaleDateString('es-DO', { dateStyle: 'medium' })
      : capitalize(date.toLocaleDateString('es-DO', { month: 'short', year: 'numeric' }));
  }

  function formatCurrency(value: number) {
    const formattedNumber = new Intl.NumberFormat('es-DO').format(value);
    return `RD$ ${formattedNumber}`;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.salesItems}>
          <div className={style.item}>
            <span className={style.title}>Balance</span>
            <span className={style.amount}>{formatCurrency(salesState.balance)}</span>
          </div>
          <div className={style.item}>
            <span className={style.title}>Ventas</span>
            <span className={style.amount}>{formatCurrency(salesState.sales)}</span>
          </div>
          <div className={style.item}>
            <span className={style.title}>Beneficio</span>
            <span className={style.amount}>{formatCurrency(salesState.profit)}</span>
          </div>
        </div>
        <div className={style.options}>
          <DateRange
            value={dateRange}
            options={options}
            onChange={handleDateRangeChange}/>
        </div>
      </div>
      <div className={style.content}>
        <div className={style.chartsContainer}>
          <div className={style.topCharts}>
            <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
              <div className={style.chartTitle}>Ventas</div>
              {sales.length > 0 &&
                <BarChart
                  data={sales}
                  formatX={formatDate}
                  formatY={formatCurrency}
                  bars={[{ 
                    key: 'sales', 
                    title: 'Ventas', 
                    color: 'var(--color-emerald-500)'
                  }]}/>
              }
              {!sales.length &&
                <div className={style.empty}>No hay datos para mostrar</div>
              }
            </OutlineCard>
            <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
              <div className={style.chartTitle}>Beneficios</div>
              {profits.length > 0 &&
                <BarChart
                  data={profits}
                  formatX={formatDate}
                  formatY={formatCurrency}
                  bars={[{ 
                    key: 'profit', 
                    title: 'Beneficios', 
                    color: 'var(--color-emerald-500)'
                  }]}/>
              }
              {!profits.length &&
                <div className={style.empty}>No hay datos para mostrar</div>
              }
            </OutlineCard>
          </div>
          <div className={style.bottomChart}>
            <OutlineCard className={clsx(style.chartCard, loading && style.chartCardloading)}>
              <div className={style.chartTitle}>Ventas por producto</div>
              {salesByProduct.length > 0 && 
                <AreaChart
                  data={salesByProduct}
                  showLegend={true}
                  formatX={formatDate}
                  formatY={formatCurrency}
                  areas={[
                    { key: 'pin', title: 'Pines', color: 'var(--color-amber-500)', fillColor: 'var(--color-amber-400)' },
                    { key: 'invoice', title: 'Facturas', color: 'var(--color-blue-500)', fillColor: 'var(--color-blue-400)' },
                    { key: 'dataPlan', title: 'Paqueticos', color: 'var(--color-purple-500)', fillColor: 'var(--color-purple-400)' },
                    { key: 'topup', title: 'Recargas', color: 'var(--color-emerald-500)', fillColor: 'var(--color-emerald-400)' },
                    { key: 'insurance', title: 'Seguros', color: 'var(--color-orange-500)', fillColor: 'var(--color-emerald-400)' },
                  ]}/>
              }
              {!salesByProduct.length &&
                <div className={style.empty}>No hay datos para mostrar</div>
              }
            </OutlineCard>
          </div>
        </div>
      </div>
    </div>
  );
}
